import {Injectable} from '@angular/core';
import {RequestService} from "../request/request.service";
import {LegacyInitializer} from "../initialization/legacy.initializer";
import {interval, Subscription} from 'rxjs';

import {FeatureFlagsService} from "../feature-flag/feature-flags.service";

import * as fastDeepEqual from 'fast-deep-equal/es6';
import {debounce, memoize, wrap} from 'lodash';
import {UtilService} from "../util/util.service";
import {AuditHistoryService} from "../audit-history/audit-history.service";

@Injectable({
  providedIn: 'root'
})
export class RecordSyncService {

  forms: any = {};

  intervalSubscription: Subscription;

  ignoredFieldTypes = ['password', 'encrypted_password', 'encrypted_string'];

  constructor(private requestService: RequestService,
              private featureFlagsService: FeatureFlagsService,
              private auditHistoryService: AuditHistoryService,
              private legacyInitializer: LegacyInitializer) {
  }

  init() {

    const self = this;

    this.intervalSubscription = interval(60000).subscribe(val => this.keepAlive());

    const messageBus = (window as any).KO_MESSAGE_BUS_BRIDGE;

    // Form load and unload events
    messageBus.subscribe(messageBus.CHANNELS.FORM_API_EVENT,
      "#",
      async (data: any, envelope: any) => {
        const disable = self.featureFlagsService.isOn("disable-record-sync");
        const debug = self.featureFlagsService.isOn("record-sync-debugging-display");

        if (disable) {
          return;
        }

        if (debug) {
          if (data?._eventType !== 'onChange') {
            console.log("RecordSync: event " + messageBus.CHANNELS.FORM_API_EVENT + " envelope:", envelope);
            console.log(data);
          }
        }

        if (envelope.topic.startsWith("onLoad") && data._table && data.current) {
          // Load of a DetailsViewModel

          const table = data._table;
          const id = data.current._data._mappedData.id();

          // We cannot use data._formInstance as it is generated anew after a save&stay or refresh so the number
          // of records in this.forms just keeps growing.
          // It is unlikely that the same record will appear in two different forms on the same page.
          // const formInstance = data._formInstance;
          const formInstance = `${table}.${id}`;

          const current = data.current;
          const viewModel = data.viewModel;
          const viewModelData = viewModel.data;

          if (typeof data.viewModel.isRecordSyncDisabled == "function") {
              if (data.viewModel.isRecordSyncDisabled()) {
                debug && console.log(`RecordSync: Sync disabled for ${formInstance}`, viewModel);
                return;
              }
          }

          debug && console.log(`RecordSync: register ${table} ${id} ${formInstance}`, viewModel);

          // Wipe out the RecordSyncData as this viewModel might be reused if a save and stay has been done.
          Object.getOwnPropertyNames(viewModelData).forEach(fieldName => {
            if (viewModelData[fieldName].recordSyncData) {
              viewModelData[fieldName].recordSyncData(null);
            }
          });
          viewModel.recordSyncData(null);

          // Copy this piece of state across as onLoad can be a result of save&stay, and we will need it for checking
          // journal changes.
          const lastSubmittedJournalValues = this.forms[formInstance] ? this.forms[formInstance].lastSubmittedJournalValues : null;

          this.forms[formInstance] = {
            table: table,
            id: id,
            formInstance: formInstance,
            current: current,
            viewModel: viewModel,
            submitting: false,
            submitCount: 0
          };

          if (lastSubmittedJournalValues) {
            this.forms[formInstance].lastSubmittedJournalValues = lastSubmittedJournalValues;
          }

          const requestData = {
            table: table,
            id: id,
            formInstance: formInstance
          };

          await this.sendRequest(requestData, "register", );

        } else if (envelope.topic === "UNLOAD" && data.table && data.id) {
          // Unload of a DetailsViewModel
          const table = data.table;
          const id = data.id;
          const formInstance = data.formInstance;

          debug && console.log(`RecordSync: deregister ${table} ${id} ${formInstance}`);

          delete this.forms[formInstance];

          const requestData = {
            table: table,
            id: id,
            formInstance: formInstance
          };

          await this.sendRequest(requestData, "deregister");

        } else if (envelope.data._eventType === "afterSubmit") {
          // Submit on a form
          const submittedForm = self.forms[envelope.data._formInstance];
          if (submittedForm) {

            // Record sync sends back journal changes to all forms. We attempt to detect if we submitted them by checking
            // the returned value against the last submitted ones by this form, as it is hard to tell otherwise.
            const journalFields: string[] = Object.keys(submittedForm.current).filter((k) => !k.startsWith('_')).filter((k) => submittedForm.current[k]?.fieldType?.() == 'journal');
            const journalValues = journalFields.reduce((acc, next) => { acc[next] = submittedForm.current[next](); return acc; }, {});
            submittedForm.lastSubmittedJournalValues = journalValues;

            const submitCount = submittedForm.submitCount + 1;
            submittedForm.submitting = true;
            submittedForm.submitCount = submitCount;
            debug && console.log(`RecordSync: submitting with count ${submitCount}...`);
            setTimeout(() => {
              // We have no way of knowing if a submit failed so we can't reset the submitting flag if that is the
              // case. In the success case the onLoad tells us the submit was successful. We wait 30 seconds to see if the last submit failed or not.
              // This will be case if there has not been another submit in the interim and it thinks it is still submitting.
              // If there has been another submit a new setTimeout will have been scheduled.
              if (submittedForm.submitCount === submitCount && submittedForm.submitting === true) {
                submittedForm.submitting = false;
                console.log(`RecordSync: clearing the submitting flag with count ${submitCount} as it wasn't cleared for 30s`);
              }
            }, 1000 * 30);
          }
        }
      }
    );

    // Table events
    messageBus.subscribe(messageBus.CHANNELS.TABLE_EVENT,
      "#",
      async (data: any, envelope: any) => {
        const debug = self.featureFlagsService.isOn("record-sync-debugging-display");
        if (debug) {
          console.log("RecordSync: event " + messageBus.CHANNELS.TABLE_EVENT + " envelope:", envelope);
          console.log(data);
        }
      });

    // Sync broadcasts from the server - these need to be debounced.
    const recordSyncHandler = (data: any) => {
      const disable = self.featureFlagsService.isOn("disable-record-sync");
      const debug = self.featureFlagsService.isOn("record-sync-debugging-display");

      if (disable) {
        return;
      }

      debug && console.log("RecordSync: receive message from server", data);

      messageBus.publish(messageBus.CHANNELS.TABLE_EVENT, data.table + ".sync", { id: data.id });

      const id = data.id;
      const userId = data.userId;
      const firstName = data.firstName;
      const lastName = data.lastName;
      const userName = data.userName;
      const version = data.version;
      const displayName = firstName + " " + lastName;
      const changes = data.changes;
      let journalFieldUpdate = [];
      let hasWorkJournal = false;
      let forceFormFooterReload = data.reloadFormFooter === true;

      const activeForms = Object.getOwnPropertyNames(self.forms).map((formInstance) => self.forms[formInstance].id);

      debug && console.log("RecordSync: we have forms for records " + activeForms);

      Object.getOwnPropertyNames(self.forms).forEach(formInstance => {
        const form = self.forms[formInstance];

        // For audit history
        hasWorkJournal = !!form.viewModel.data['WorkJournal'];

        if (form.id === id && !form.submitting) {
          // if (debug) { console.log(`RecordSync: Processing form form.submitting=[${form.submitting} form.id=${form.id}]`); }

          Object.getOwnPropertyNames(changes).forEach((fieldName => {
            const fieldViewModel = form.viewModel.data[fieldName];
            const fieldType = fieldViewModel?.fieldType;
            const fieldTableRecord = form.current.getField(fieldName);

            const ignoreField = self.ignoredFieldTypes.includes(fieldType);

            if (ignoreField) {
              debug && console.log(`RecordSync: ignoring field ${fieldName} as it is of type ${fieldType}`);
            }

            if (fieldViewModel && !ignoreField) {
              // if (debug) { console.log(`RecordSync: Processing form field: fieldViewModel`, fieldViewModel); }
              const currentValue = fieldViewModel.value();

              const change = changes[fieldName];

              const recordSyncData = {
                displayName: displayName,
                value: change.value,
                displayValue: change.displayValue,
                conflict: false,
                formatSuccess: function () {
                  return `${displayName} has changed the value to ${change.displayValue}`;
                },
                formatConflict: function () {
                  return `${displayName} has changed the value to ${change.displayValue} but you have also changed the value on this form to ${currentValue}`;
                },
                formatMessage: function () {
                  return this.conflict ? this.formatConflict() : this.formatSuccess()
                },
                formatIcon: function () {
                  return "sync_alt";
                }
              };

              // The table_array and array_string types look like scalars but are actually arrays
              const scalarOrArrayValuesDoNotMatch = (current: any, change: any, fieldType: string): boolean => {
                if (fieldType.toLowerCase() === "table_array" || fieldType.toLowerCase() === "array_string") {
                  if (Array.isArray(current) && Array.isArray(change)) {
                    return !fastDeepEqual(current, change);
                  } else {
                    return current !== change;
                  }
                } else if (fieldType.toLowerCase() === "date_time" || fieldType.toLowerCase() === "date") {
                  // This is necessary because sometimes we get timezone information like +HHMM and sometimes like +HH:MM
                  if ((!current && change) || (current && !change)) {
                    return true;
                  } else {
                    try {
                      const currentDate = new Date(current).getTime();
                      const changeDate = new Date(change).getTime();
                      return currentDate !== changeDate;
                    } catch (ex) {
                      console.error(`RecordSync: dates cannot be compared '${current}' '${change}`, ex);
                      return true;
                    }
                  }
                } else {
                  return current !== change;
                }
              };

              if (fieldViewModel._type === "FieldViewModel" && fieldType === "journal") {
                // if (debug) { console.log(`RecordSync: Processing form field: fieldViewModel branch 1`); }

                // Check if the value coming to us is the last submitted value, and ignore it if it is the same as what we sent.
                const lastSubmittedValue = form?.lastSubmittedJournalValues?.[fieldViewModel.getName()];

                if (change.value !== lastSubmittedValue) {
                  // We need to change the messages as the individual entries are not important
                  recordSyncData.formatSuccess = (): string => {
                    return `${displayName} has changed the journal`;
                  };
                  recordSyncData.formatConflict = (): string => {
                    return `${displayName} has changed the journal`;
                  };

                  journalFieldUpdate.push({
                    table: form.table,
                    id: id,
                    displayName: displayName,
                    fieldName: fieldViewModel.getName(),
                    recordSyncData: recordSyncData
                  });
                  debug && console.log(`RecordSync: journal update required for ${form.table} ${id}`);
                } else {
                  debug && console.log(`RecordSync: journal update ignored for ${form.table} ${id}`);
                }
              } else if (fieldViewModel._type === "FieldViewModel") {
                // if (debug) { console.log(`RecordSync: Processing form field: fieldViewModel branch 2`); }

                // Change the messages for HTML fields as the standard ones become unreadable for HTML fields
                if (fieldViewModel.getBaseFieldDefinition()?.getRenderer() == 'html' || fieldViewModel.getBaseFieldDefinition()?.getRenderer() == 'i18nhtml') {
                  recordSyncData.formatSuccess = function () {
                    return `${displayName} has changed the value of the HTML field`;
                  };
                  recordSyncData.formatConflict = function () {
                    return `${displayName} has changed the value of the HTML field but you have also changed the value on this form of the HTML field`;
                  };
                }

                if (scalarOrArrayValuesDoNotMatch(currentValue, change.value, fieldViewModel.fieldType)) {

                  debug && console.log(`RecordSync: scalar values don't match for field ${fieldName}`, currentValue, change.value);

                  if (fieldTableRecord.hasChanged()) {

                    debug && console.log(`RecordSync: scalar values don't match for field ${fieldName} - inconsistent`);

                    recordSyncData.conflict = true;
                    fieldViewModel.recordSyncData(recordSyncData);
                  } else {

                    debug && console.log(`RecordSync: scalar value for field ${fieldName} unchanged locally - accept change`);

                    fieldViewModel.value(change.value);
                    fieldViewModel.value.setOriginalValue(change.value);

                    fieldViewModel.displayValue(change.displayValue !== undefined ? change.displayValue : change.value);

                    fieldViewModel.value.hasChanged(false);

                    fieldViewModel.recordSyncData(recordSyncData);

                  }
                  // We want to reload the footer every time there is a change, as TableOperations and Workflow Transitions can be conditional based on field values
                  forceFormFooterReload = true;
                } else {
                  debug && console.log(`RecordSync: Scalar values match for field ${fieldName}`, currentValue);
                }
              } else if (fieldViewModel._type === "ReferenceFieldViewModel" && fieldViewModel.fieldType === "reference") {
                // if (debug) { console.log(`RecordSync: Processing form field: fieldViewModel branch 3`); }
                // WARNING - currentValue.id can be a string or an observable
                if (currentValue && UtilService.unwrapObservable(currentValue.id) !== change.value) {

                  debug && console.log(`RecordSync: reference values don't match for field ${fieldName}`, currentValue, change.value);

                  // We want to reload the footer every time there is a change, as TableOperations and Workflow Transitions can be conditional based on field values
                  forceFormFooterReload = true;

                  if (fieldTableRecord.hasChanged()) {
                    const displayValueField = form.current._data._tableDefinition.getRelation(fieldName)._data._properties['default.displayValue'] || 'id';

                    debug && console.log(`RecordSync: reference Values don't match for field ${fieldName} - inconsistent`);

                    recordSyncData.conflict = true;
                    // Special case for references, the message has to change.
                    recordSyncData.formatConflict = (): string => {
                      return `${displayName} has changed the value to ${change.displayValue} but you have also changed the value on this form to ${currentValue[displayValueField]}`;
                    };

                    fieldViewModel.recordSyncData(recordSyncData);
                  } else {
                    const displayValueField = form.current._data._tableDefinition.getRelation(fieldName)._data._properties['default.displayValue'] || 'id';

                    debug && console.log(`RecordSync: reference value unchanged locally for field ${fieldName} - accept change`);

                    if (fieldViewModel._data && (fieldViewModel._data._relationType === "many_to_one" || fieldViewModel._data._relationType === "many_to_many")) {

                      const renderer = form.viewModel._viewDefinition.fields[fieldViewModel.getName()].renderer;

                      if (renderer === 'relatedlist') {
                        messageBus.publish(messageBus.CHANNELS.TABLE_EVENT, data.table + ".refresh", { id: data.id });
                      } else if (renderer === "onetomany") {
                        const newValue = change.value.split(",").map((v, idx) => {
                          const displayValue = change.displayValue.split(",");
                          return {
                            id: v,
                            _tableClass: fieldViewModel.getTargetTableName(),
                            value: {
                              value: displayValue[idx],
                              displayValue: displayValue[idx],
                              required: false,
                              readOnly: false
                            }
                          };
                        });

                        fieldViewModel.value(newValue);
                        fieldViewModel.value.setOriginalValue(newValue);
                        fieldViewModel.value.hasChanged(false);
                      }

                    } else {
                      const newValue = {
                        TableClass: fieldViewModel.getTargetTableName(),
                        id: change.value,
                      };

                      newValue[displayValueField] = change.displayValue;

                      fieldViewModel.value(newValue);
                      fieldViewModel.value.setOriginalValue(newValue);
                      fieldViewModel.value.hasChanged(false);
                    }

                    fieldViewModel.recordSyncData(recordSyncData);
                  }
                } else if (fieldViewModel._data && (fieldViewModel._data._relationType === "many_to_one" || fieldViewModel._data._relationType === "many_to_many")) {
                  // Just reload the many to X forms
                  messageBus.publish(messageBus.CHANNELS.TABLE_EVENT, data.table + ".refresh", { id: data.id });
                } else {
                  debug && console.log(`RecordSync: reference values match for field ${fieldName}`, currentValue);
                }
              } else {
                if (debug) { console.log(`RecordSync: Processing form field: fieldViewModel NO branch`); }
              }
            } else {
              debug && console.log(`RecordSync: no fieldViewModel for field ${fieldName}`);
            }
          }));

          // Process the Form Footer Reload if requested
          if (forceFormFooterReload) {
            const data = {
              id: form.id,
              table: form.table
            };
            this.sendRequest(data, "getCurrentWorkflowState")
              .then((result) => {
                const workflowData = JSON.parse(result);
                const mappedWorkflow = (window as any).KO_MAPPING.fromJS(workflowData, {}, {});
                if (mappedWorkflow._workflow) {
                  form.current._data._mappedData._workflow.activityType(mappedWorkflow._workflow.activityType());
                  form.current._data._mappedData._workflow.workflowTask.workflowTaskID(mappedWorkflow._workflow.workflowTask.workflowTaskID());
                  form.current._data._mappedData._workflow.workflowTask.type(mappedWorkflow._workflow.workflowTask.type());
                  form.current._data._mappedData._workflow.transitions(mappedWorkflow._workflow.transitions());
                }
                // Because we have likely modified the transitions, we need to alert the footer
                messageBus.publish(messageBus.CHANNELS.FORM_API_EVENT, `footer.reload`, { id: form.id });

              })
              .catch((error) => {
                console.error("RecordSync: error while attempting to get current workflow state", error);
              });
          }

          const viewModelData = form.viewModel.data;
          let hasChanges = false;
          let hasConflicts = false;
          Object.getOwnPropertyNames(viewModelData).forEach((fieldName) => {
            const fieldViewModel = viewModelData[fieldName];
            if (fieldViewModel.recordSyncData && fieldViewModel.recordSyncData() !== null) {
              hasChanges = true;
              if (fieldViewModel.recordSyncData().conflict === true) {
                hasConflicts = true;
              }
            }
          });

          // if (debug) { console.log(`RecordSync: hasChanges=[${hasChanges}] journalFieldUpdate.length=[${journalFieldUpdate.length}]`); }
          if (hasChanges || journalFieldUpdate.length > 0) {

            const recordSyncData = {
              displayName: displayName,
              value: null,
              displayValue: null,
              conflict: hasConflicts,
              formatSuccess: function () {
                return `${displayName} has changed fields on this form`;
              },
              formatConflict: function () {
                return `${displayName} has changed fields on this form but there are conflicts with your changes`;
              },
              formatMessage: function () {
                return this.conflict ? this.formatConflict() : this.formatSuccess();
              },
              formatIcon: function () {
                return "sync_alt";
              }
            };

            form.viewModel.recordSyncData(recordSyncData);

            // We need to request the audit history stuff again, if we have a WorkJournal and there are updates
            if (hasWorkJournal && journalFieldUpdate.length > 0) {
              debug && console.log(`AuditHistory: Record-sync: requestAuditHistory: ${id} : WorkJournal`);

              this.auditHistoryService.requestAuditHistory({tableName: form.table, id: id, fieldName: "WorkJournal"});
            }

          } else {
            form.viewModel.recordSyncData(null);
          }

        }
      });


      if (journalFieldUpdate.length > 0) {
        // Refresh the journal fields by sending a message
        journalFieldUpdate.forEach((update) => {

          debug && console.log(`RecordSync: sending journal update message for ${update.table} ${update.id} ${update.fieldName}`);

          messageBus.publish(messageBus.CHANNELS.TABLE_EVENT,
            `${update.table}.updateJournal`,
            {
              id: update.id,
              displayName: update.displayName,
              fieldName: update.fieldName,
              recordSyncData: update.recordSyncData
            });
        });
      }


    };

    // This implementation uses the set of changed fields to determine if debouncing should occur. Therefore there
    // will be a memoized debounce function for each possible set of changed fields.
    const memoizeKey = (data: any) => Object.keys(data.changes).sort().join('-');
    const debounceDelay = 500; // ms
    const debounceOptions = {}; // Options are 'leading' and 'trailing'. Defaults seem to work ok.
    const debouncedRecordSyncHandler = wrap(memoize(() => debounce(recordSyncHandler, debounceDelay, debounceOptions), memoizeKey),
                                            (getMemoizedFunction, data) => getMemoizedFunction(data)(data));

    (window as any).WebsocketAPI.registerAPIImplementation("RecordSync", debouncedRecordSyncHandler);
  }

  async sendRequest(requestData: any, method: string) {
    try {
      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.RecordSync")
        .method(method)
        .data(requestData)
        .execute();

      const responseData = response.data;

      if (responseData) {
        return responseData;
      }
    } catch (e) {
      console.error("RecordSync: exception sending request", e);
      return null;
    }
  }

  async keepAlive() {
    const disable = this.featureFlagsService.isOn("disable-record-sync");
    const debug = this.featureFlagsService.isOn("record-sync-debugging-display");

    if (disable) {
      return;
    }

    if (!this.forms) {
      return;
    }

    const data = {
      forms: Object.getOwnPropertyNames(this.forms)
        // Do not request sync for record creation forms
        .filter(formInstance => !this.forms[formInstance]?.current?.isNewRecord())
    };

    debug && console.log("RecordSync: keepAlive we have forms for records", data.forms);

    if (data.forms.length > 0) {
      await this.sendRequest(data, "keepAlive");
    }
  }
}
